import type { ApiErrorBody, AuthTokens } from '@motoschool/shared';
import { buildUrl, resolveApiBase, type QueryValue } from './url';

// Опциональная цепочка нужна вне сборки Vite (тесты в Node), где import.meta.env нет
const API_URL =
  ((import.meta.env as ImportMetaEnv | undefined)?.VITE_API_URL as string | undefined) ??
  'http://localhost:4000';

const API_BASE = resolveApiBase(
  API_URL,
  typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
);

const ACCESS_KEY = 'motoschool.access';
const REFRESH_KEY = 'motoschool.refresh';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Ошибки полей формы, если сервер их прислал. */
  get fieldErrors(): Record<string, string> {
    return this.details && typeof this.details === 'object'
      ? (this.details as Record<string, string>)
      : {};
  }
}

export const tokenStorage = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  save(tokens: AuthTokens): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Пока идёт обновление токена, параллельные запросы ждут один и тот же промис.
 * Иначе десяток одновременных 401 сожгли бы refresh-токен, который одноразовый.
 */
let refreshPromise: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokenStorage.refresh;
  if (!refreshToken) return false;

  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        tokenStorage.clear();
        return false;
      }

      const data = (await response.json()) as { tokens: AuthTokens };
      tokenStorage.save(data.tokens);
      return true;
    } catch {
      return false;
    } finally {
      // Сбрасываем в микрозадаче, чтобы ожидающие успели прочитать результат.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Публичные роуты не должны получать заголовок авторизации. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    const access = tokenStorage.access;
    if (access && !options.anonymous) headers.Authorization = `Bearer ${access}`;

    return fetch(buildUrl(API_BASE, path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let response = await send();

  // Один прозрачный повтор после обновления токена: пользователь не должен
  // видеть разлогин каждые пятнадцать минут.
  if (response.status === 401 && !options.anonymous && tokenStorage.refresh) {
    const refreshed = await refreshTokens();
    if (refreshed) response = await send();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiRequestError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? 'Что-то пошло не так, попробуйте ещё раз',
      body?.error?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

export { API_URL, API_BASE };
